/**
 * product-picker.tsx — Nút "Sản phẩm" ở thanh công cụ khung soạn tin + bảng chọn sản phẩm để gửi khách.
 *
 * Nguồn: sản phẩm đồng bộ từ nền tảng bán hàng (Evotech…) và sản phẩm tạo trong eCDP, chia tab theo
 * nguồn (chỉ nguồn có hàng). Biến thể cùng nhóm gộp một dòng + bộ chọn biến thể.
 * Gửi sản phẩm Evotech: chọn link Webapp hoặc Mini App — link do máy chủ dựng theo mẫu của tổ chức
 * (POST /conversations/:id/send-product); ở đây chỉ điền mẫu để XEM TRƯỚC.
 */
import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { toast } from 'sonner'
import { useTranslation } from 'react-i18next'
import { Check, ChevronDown, Globe, ImageOff, Loader2, PlugZap, Search, Send, ShoppingBag, Smartphone, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { ComposerToolTrigger, type ToolTriggerMode } from '@/components/shared/composer-tool-trigger'
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { apiError } from '@/lib/api-client'
import { currentLocale } from '@/lib/i18n'
import { cn } from '@/lib/utils'
import { useAuthStore } from '@/stores/auth-store'
import {
  resolveImageUrl, useProductPickerList, useProductPickerSources, useSendProduct,
  type Product, type ProductPickerSources,
} from '@/hooks/use-products'
import {
  buildSourceTabs, defaultLinkType, fillLinkTemplate, formatGroupPrice, formatPickerPrice, groupProducts,
  productLinkValues, readLastLinkType, saveLastLinkType, stockLabel, variantLabel, SOURCE_LABELS,
  type LinkType, type ProductGroup,
} from './product-picker-lib'

/** Trang cài đặt kết nối + mẫu link Evotech. */
export const EVOTECH_SETTINGS_PATH = '/integrations/apps/evotech'

export function ProductPickerButton({ convId, disabled, disabledHint, mode }: { convId: string; disabled?: boolean; disabledHint?: string; mode?: ToolTriggerMode }) {
  const { t } = useTranslation('convCommerce')
  const [open, setOpen] = useState(false)
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        {mode ? (
          <ComposerToolTrigger mode={mode} icon={ShoppingBag} label={t('picker.button')} disabled={disabled} title={disabled ? disabledHint : t('picker.sendToCustomer')} />
        ) : (
          <Button
            variant="outline"
            size="sm"
            className="h-8 gap-1.5 px-2.5 text-xs"
            disabled={disabled}
            title={disabled ? disabledHint : t('picker.sendToCustomer')}
            aria-label={t('picker.button')}
          >
            <ShoppingBag className="h-4 w-4 text-primary" />
            {t('picker.button')}
          </Button>
        )}
      </PopoverTrigger>
      <PopoverContent
        side="top"
        align="end"
        // max-h theo khoảng trống Radix tính sẵn: mở từ ô giữa màn (hộp Tiện ích trên điện thoại) không bị cắt mép trên.
        collisionPadding={8}
        className="flex h-[min(520px,75vh)] max-h-[var(--radix-popover-content-available-height)] w-[min(460px,calc(100vw-16px))] flex-col overflow-hidden p-0"
      >
        {open && <PickerBody convId={convId} onSent={() => setOpen(false)} onClose={() => setOpen(false)} />}
      </PopoverContent>
    </Popover>
  )
}

function useDebounced<T>(value: T, ms: number): T {
  const [v, setV] = useState(value)
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms)
    return () => clearTimeout(t)
  }, [value, ms])
  return v
}

function PickerBody({ convId, onSent, onClose }: { convId: string; onSent: () => void; onClose: () => void }) {
  const { t } = useTranslation('convCommerce')
  const { data: meta, isLoading: metaLoading } = useProductPickerSources(true)
  const tabs = useMemo(() => buildSourceTabs(meta?.sources ?? []), [meta])
  const [tab, setTab] = useState<string | null>(null)
  const active = tab && tabs.some((x) => x.key === tab) ? tab : tabs[0]?.key ?? null
  const [search, setSearch] = useState('')
  const q = useDebounced(search.trim(), 300)

  const list = useProductPickerList({ externalSource: active ?? undefined, search: q || undefined }, !!active)
  const items = useMemo(() => list.data?.pages.flatMap((p) => p.items) ?? [], [list.data])
  const groups = useMemo(() => groupProducts(items), [items])
  const total = list.data?.pages[0]?.meta.total ?? 0

  return (
    <>
      {/* Đầu bảng: tiêu đề + tab nguồn (hoặc nhãn nguồn khi chỉ có một) */}
      <div className="border-b px-3 pt-3">
        <div className="flex items-center gap-2">
          <ShoppingBag className="h-4 w-4 text-primary" />
          <h3 className="text-sm font-semibold">{t('picker.title')}</h3>
          {tabs.length === 1 && (
            <span className="rounded-full bg-muted px-2 py-0.5 text-[11px] text-muted-foreground">
              {tabs[0].label} · {tabs[0].count}
            </span>
          )}
          <button type="button" onClick={onClose} aria-label={t('common:actions.close')} className="ml-auto text-muted-foreground hover:text-foreground">
            <X className="h-4 w-4" />
          </button>
        </div>
        {tabs.length > 1 ? (
          <div role="tablist" aria-label={t('picker.sourceAria')} className="-mb-px mt-2 flex gap-4 overflow-x-auto">
            {tabs.map((x) => (
              <button
                key={x.key}
                type="button"
                role="tab"
                aria-selected={x.key === active}
                onClick={() => setTab(x.key)}
                className={cn(
                  'shrink-0 border-b-2 pb-2 text-xs transition-colors',
                  x.key === active ? 'border-primary font-medium text-primary' : 'border-transparent text-muted-foreground hover:text-foreground',
                )}
              >
                {x.label} <span className="tabular-nums opacity-70">({new Intl.NumberFormat(currentLocale()).format(x.count)})</span>
              </button>
            ))}
          </div>
        ) : (
          <div className="h-3" />
        )}
      </div>

      {metaLoading ? (
        <CenterNote><Loader2 className="h-4 w-4 animate-spin" /> {t('picker.loading')}</CenterNote>
      ) : !active ? (
        <NotConnected meta={meta} />
      ) : (
        <>
          <div className="border-b p-2">
            <div className="relative">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
              <Input
                autoFocus
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={t('picker.searchPlaceholder')}
                className="h-8 pl-8 text-xs"
              />
            </div>
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto">
            {list.isLoading ? (
              <CenterNote><Loader2 className="h-4 w-4 animate-spin" /> {t('picker.loadingProducts')}</CenterNote>
            ) : list.isError ? (
              <CenterNote>{t('picker.loadError')}</CenterNote>
            ) : groups.length === 0 ? (
              <CenterNote>{q ? t('picker.noMatch') : t('picker.noProducts')}</CenterNote>
            ) : (
              <ul className="divide-y">
                {groups.map((g) => (
                  <ProductRow key={g.key} group={g} convId={convId} meta={meta} onSent={onSent} />
                ))}
              </ul>
            )}
            {list.hasNextPage && (
              <div className="p-2 text-center">
                <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => list.fetchNextPage()} disabled={list.isFetchingNextPage}>
                  {list.isFetchingNextPage && <Loader2 className="h-3.5 w-3.5 animate-spin" />} {t('common:actions.showMore')}
                </Button>
              </div>
            )}
          </div>
          {total > 0 && (
            <div className="border-t px-3 py-1.5 text-[11px] text-muted-foreground">
              {t('picker.loadedCount', { loaded: items.length, total })}{active === 'evotech' ? t('picker.evotechLinkNote') : ''}
            </div>
          )}
        </>
      )}
    </>
  )
}

function CenterNote({ children }: { children: React.ReactNode }) {
  return <div className="flex flex-1 items-center justify-center gap-2 p-6 text-center text-xs text-muted-foreground">{children}</div>
}

function NotConnected({ meta }: { meta?: ProductPickerSources }) {
  const { t } = useTranslation('convCommerce')
  const connected = meta?.evotech.connected
  return (
    <div className="flex flex-1 flex-col items-center justify-center gap-2 p-6 text-center">
      <PlugZap className="h-8 w-8 text-muted-foreground/60" />
      <p className="text-sm font-medium">{connected ? t('picker.notConnected.noProductsTitle') : t('picker.notConnected.title')}</p>
      <p className="max-w-xs text-xs text-muted-foreground">
        {connected
          ? t('picker.notConnected.noProductsBody')
          : t('picker.notConnected.body')}
      </p>
      <Button asChild size="sm" variant="outline" className="mt-1 h-8 text-xs">
        <Link to={EVOTECH_SETTINGS_PATH}>{connected ? t('picker.notConnected.openSettings') : t('picker.notConnected.connect')}</Link>
      </Button>
    </div>
  )
}

function ProductRow({ group, convId, meta, onSent }: {
  group: ProductGroup; convId: string; meta?: ProductPickerSources; onSent: () => void
}) {
  const { t } = useTranslation('convCommerce')
  const [variantId, setVariantId] = useState(group.variants[0].id)
  const p = group.variants.find((v) => v.id === variantId) ?? group.variants[0]
  const multi = group.variants.length > 1
  const img = resolveImageUrl(p.images[0] ?? group.variants.find((v) => v.images[0])?.images[0])
  const stock = stockLabel(p.stockAvailable)
  const sourceLabel = p.externalSource ? SOURCE_LABELS[p.externalSource] ?? p.externalSource : null

  return (
    <li className="flex gap-3 px-3 py-2.5">
      <div className="flex h-14 w-14 shrink-0 items-center justify-center overflow-hidden rounded-md border bg-muted/40">
        {img ? <img src={img} alt="" loading="lazy" className="h-full w-full object-cover" /> : <ImageOff className="h-5 w-5 text-muted-foreground/50" />}
      </div>
      <div className="min-w-0 flex-1">
        <p className="line-clamp-2 text-[13px] font-medium leading-snug">{multi ? group.title : p.name}</p>
        <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[11px] text-muted-foreground">
          {p.code && <span className="font-mono">{p.code}</span>}
          {sourceLabel && <span className="rounded bg-primary/10 px-1.5 py-px text-[10px] font-medium text-primary">{sourceLabel}</span>}
          {stock && (
            <span className={cn(stock.tone === 'out' ? 'text-destructive/80' : 'text-emerald-600 dark:text-emerald-400')}>{stock.text}</span>
          )}
        </div>
        {multi && (
          <select
            value={variantId}
            onChange={(e) => setVariantId(e.target.value)}
            aria-label={t('picker.chooseVariant')}
            className="mt-1 h-7 max-w-full rounded-md border bg-background px-1.5 text-[11px]"
          >
            {group.variants.map((v) => (
              <option key={v.id} value={v.id}>
                {variantLabel(v.name, group.title)} — {formatPickerPrice(v)}
                {v.stockAvailable != null && v.stockAvailable <= 0 ? t('picker.soldOutSuffix') : ''}
              </option>
            ))}
          </select>
        )}
      </div>
      <div className="flex shrink-0 flex-col items-end justify-between gap-1">
        <span className="text-[13px] font-semibold tabular-nums">{multi ? formatPickerPrice(p) : formatGroupPrice([p])}</span>
        <SendButton product={p} convId={convId} meta={meta} onSent={onSent} />
      </div>
    </li>
  )
}

function SendButton({ product, convId, meta, onSent }: {
  product: Product; convId: string; meta?: ProductPickerSources; onSent: () => void
}) {
  const { t } = useTranslation('convCommerce')
  const userId = useAuthStore((s) => s.user?.id)
  const send = useSendProduct(convId)

  const doSend = (linkType?: LinkType) => {
    send.mutate(
      { productId: product.id, ...(linkType ? { linkType } : {}) },
      {
        onSuccess: (r) => {
          if (linkType) saveLastLinkType(userId, linkType)
          if (r.imageError) toast.warning(t('picker.sentImageFailed', { error: r.imageError }))
          else toast.success(t('picker.sent'))
          onSent()
        },
        onError: (e) => toast.error(apiError(e)),
      },
    )
  }

  // Sản phẩm không đồng bộ từ Evotech: gửi thẳng tên + giá.
  if (product.externalSource !== 'evotech' || !product.externalId) {
    return (
      <Button size="sm" className="h-7 gap-1 px-2.5 text-xs" onClick={() => doSend()} disabled={send.isPending}>
        {send.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />} {t('common:actions.send')}
      </Button>
    )
  }

  const values = productLinkValues(product)
  const links: Record<LinkType, string | null> = {
    webapp: fillLinkTemplate(meta?.evotech.webappUrlTemplate, values),
    miniapp: fillLinkTemplate(meta?.evotech.miniAppUrlTemplate, values),
  }
  const configured: Record<LinkType, boolean> = {
    webapp: !!meta?.evotech.webappUrlTemplate,
    miniapp: !!meta?.evotech.miniAppUrlTemplate,
  }
  const last = readLastLinkType(userId)
  const preferred = defaultLinkType({ webapp: !!links.webapp, miniapp: !!links.miniapp }, last)

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button size="sm" className="h-7 gap-1 px-2.5 text-xs" disabled={send.isPending}>
          {send.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />} {t('common:actions.send')}
          <ChevronDown className="h-3 w-3 opacity-70" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" side="left" className="w-72">
        <DropdownMenuLabel className="text-xs font-normal text-muted-foreground">{t('picker.sendWithLink')}</DropdownMenuLabel>
        {(['webapp', 'miniapp'] as const).map((lt) => (
          <LinkOption
            key={lt}
            type={lt}
            url={links[lt]}
            configured={configured[lt]}
            preferred={preferred === lt}
            remembered={preferred === lt && last === lt && !!links.webapp && !!links.miniapp}
            onPick={() => doSend(lt)}
          />
        ))}
        <DropdownMenuSeparator />
        <DropdownMenuItem className="text-xs text-muted-foreground" onClick={() => doSend()}>
          {t('picker.sendNamePriceOnly')}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

const LINK_META: Record<LinkType, { labelKey: string; icon: typeof Globe }> = {
  webapp: { labelKey: 'picker.link.webapp', icon: Globe },
  miniapp: { labelKey: 'picker.link.miniapp', icon: Smartphone },
}

function LinkOption({ type, url, configured, preferred, remembered, onPick }: {
  type: LinkType; url: string | null; configured: boolean; preferred: boolean; remembered: boolean; onPick: () => void
}) {
  const { t } = useTranslation('convCommerce')
  const { labelKey, icon: Icon } = LINK_META[type]
  const label = t(labelKey)
  if (!url) {
    // Shop chưa có tên miền / Mini App (hoặc sản phẩm thiếu mã) → hiện mờ + đường tới cài đặt.
    return (
      <div className="flex items-start gap-2 rounded-sm px-2 py-1.5 text-sm opacity-60">
        <Icon className="mt-0.5 h-4 w-4 shrink-0" />
        <div className="min-w-0">
          <p>{label}</p>
          <p className="text-[11px] text-muted-foreground">
            {configured ? t('picker.link.missingCode') : type === 'webapp' ? t('picker.link.noWebapp') : t('picker.link.noMiniapp')} —{' '}
            <Link to={EVOTECH_SETTINGS_PATH} className="text-primary underline-offset-2 hover:underline">{t('common:actions.settings')}</Link>
          </p>
        </div>
      </div>
    )
  }
  return (
    <DropdownMenuItem onClick={onPick} className={cn('items-start gap-2', preferred && 'bg-primary/5')}>
      <Icon className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
      <div className="min-w-0 flex-1">
        <p className="flex items-center gap-1.5">
          {label}
          {remembered && (
            <span className="inline-flex items-center gap-0.5 text-[10px] text-primary">
              <Check className="h-3 w-3" /> {t('picker.link.lastTime')}
            </span>
          )}
        </p>
        <p className="truncate font-mono text-[10.5px] text-muted-foreground" title={url}>{url}</p>
      </div>
    </DropdownMenuItem>
  )
}
